// src/components/rep/RepPlaceOrder.js
import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { calculateTotalWithFee, calculateCCFee, applyTierDiscount } from '../../config/constants';
import { ShoppingCart, Plus, Minus, Trash2, MapPin, Loader2, CreditCard, FileText, User } from 'lucide-react';

export function RepPlaceOrder({ customers, products, onOrderPlaced }) {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [cart, setCart] = useState([]);
  const [notes, setNotes] = useState('');
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');

  // Get products with customer's discount applied
  const getDiscountedProducts = () => {
    if (!selectedCustomer) return products;
    const discount = selectedCustomer.tierDiscount || 0;
    return products.map(p => ({
      ...p,
      originalPrice: p.wholesalePrice,
      wholesalePrice: Math.round(applyTierDiscount(p.wholesalePrice, discount) * 100) / 100
    }));
  };

  const discountedProducts = getDiscountedProducts();

  const addToCart = (product) => {
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: product.minQuantity || 1 }]);
    }
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      setCart(cart.filter(item => item.id !== productId));
    } else {
      setCart(cart.map(item =>
        item.id === productId ? { ...item, quantity } : item
      ));
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.wholesalePrice * item.quantity, 0);
  const ccFee = selectedCustomer?.paymentMethod === 'customerCC' ? calculateCCFee(subtotal) : 0;
  const total = selectedCustomer?.paymentMethod === 'customerCC' ? calculateTotalWithFee(subtotal) : subtotal;

  const handlePlaceOrder = async () => {
    if (!selectedCustomer) {
      setError('Please select a customer');
      return;
    }
    if (!selectedLocation) {
      setError('Please select a shipping location');
      return;
    }
    if (cart.length === 0) {
      setError('Cart is empty');
      return;
    }

    setPlacing(true);
    setError('');

    try {
      // Prices, discount, and commission are all recomputed server-side —
      // the cart here is only a preview
      const placeOrderFn = httpsCallable(functions, 'placeOrder');
      const result = await placeOrderFn({
        customerId: selectedCustomer.id,
        items: cart.map(item => ({ id: item.id, quantity: item.quantity })),
        shippingLocation: selectedLocation,
        notes: notes
      });

      // Reset form
      setCart([]);
      setNotes('');
      setSelectedCustomer(null);
      setSelectedLocation(null);

      if (onOrderPlaced) {
        onOrderPlaced(result.data.orderId);
      }

      alert('Order placed successfully!');
    } catch (err) {
      console.error('Error placing order:', err);
      setError(err.message);
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Place Order for Customer</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Customer & Products */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <User size={18} /> Select Customer
            </h3>
            <select
              value={selectedCustomer?.id || ''}
              onChange={(e) => {
                const customer = customers.find(c => c.id === e.target.value);
                setSelectedCustomer(customer);
                setSelectedLocation(customer?.shippingLocations?.find(l => l.isDefault) || null);
                setCart([]); // Reset cart when customer changes (prices may differ)
              }}
              className="w-full px-4 py-2 border rounded-lg"
            >
              <option value="">-- Select a customer --</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.companyName} ({customer.tierDiscount || 0}% discount)
                </option>
              ))}
            </select>

            {selectedCustomer && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Contact</p>
                    <p className="font-medium">{selectedCustomer.contactName || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Payment Method</p>
                    <p className="font-medium flex items-center gap-1">
                      {selectedCustomer.paymentMethod === 'customerCC' && <><CreditCard size={14} /> Customer CC</>}
                      {selectedCustomer.paymentMethod === 'customerInvoice' && <><FileText size={14} /> Invoice</>}
                      {selectedCustomer.paymentMethod === 'repCC' && <><CreditCard size={14} /> Your CC</>}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Discount</p>
                    <p className="font-medium text-green-600">{selectedCustomer.tierDiscount || 0}% off</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Orders</p>
                    <p className="font-medium">{selectedCustomer.totalOrders || 0}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Shipping Location */}
          {selectedCustomer && selectedCustomer.shippingLocations?.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <MapPin size={18} /> Shipping Location
              </h3>
              <div className="space-y-2">
                {selectedCustomer.shippingLocations.map((location, index) => (
                  <label
                    key={index}
                    className={`block p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedLocation === location
                        ? 'border-black bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="shippingLocation"
                        checked={selectedLocation === location}
                        onChange={() => setSelectedLocation(location)}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium">
                          {location.name}
                          {location.isDefault && <span className="text-green-600 text-sm ml-2">(Default)</span>}
                        </p>
                        <p className="text-sm text-gray-600">{location.street}</p>
                        <p className="text-sm text-gray-600">{location.city}, {location.state} {location.zip}</p>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Products */}
          {selectedCustomer && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <ShoppingCart size={18} /> Products
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {discountedProducts.map(product => (
                  <div key={product.id} className="border rounded-lg p-4">
                    <div className="flex gap-4">
                      {product.image && (
                        <img src={product.image} alt="" className="w-16 h-16 object-cover rounded" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium">{product.name}</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-green-600 font-bold">${product.wholesalePrice.toFixed(2)}</span>
                          {product.originalPrice !== product.wholesalePrice && (
                            <span className="text-sm text-gray-400 line-through">${product.originalPrice.toFixed(2)}</span>
                          )}
                        </div>
                        <button
                          onClick={() => addToCart(product)}
                          className="mt-2 px-3 py-1 bg-black text-white text-sm rounded hover:bg-gray-800"
                        >
                          Add to Order
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Cart */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6 sticky top-4">
            <h3 className="font-semibold mb-4">Order Summary</h3>

            {cart.length === 0 ? (
              <p className="text-gray-500 text-sm">No items in cart</p>
            ) : (
              <>
                <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                  {cart.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{item.name}</p>
                        <p className="text-xs text-gray-500">${item.wholesalePrice.toFixed(2)} each</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-6 h-6 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="w-8 text-center text-sm">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-6 h-6 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                        >
                          <Plus size={12} />
                        </button>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="w-6 h-6 flex items-center justify-center text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  {ccFee > 0 && (
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>CC Fee</span>
                      <span>${ccFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                </div>
              </>
            )}

            {/* Notes */}
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">Order Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Special instructions..."
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />
            </div>

            {/* Place Order Button */}
            <button
              onClick={handlePlaceOrder}
              disabled={placing || cart.length === 0 || !selectedCustomer || !selectedLocation}
              className="w-full mt-4 bg-black text-white py-3 rounded-lg hover:bg-gray-800 disabled:bg-gray-400 flex items-center justify-center gap-2"
            >
              {placing ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Placing Order...
                </>
              ) : (
                'Place Order'
              )}
            </button>

            {selectedCustomer?.paymentMethod === 'customerInvoice' && (
              <p className="text-xs text-gray-500 text-center mt-2">
                Customer will be invoiced for this order
              </p>
            )}
            {selectedCustomer?.paymentMethod === 'repCC' && (
              <p className="text-xs text-yellow-600 text-center mt-2">
                This will be charged to your card on file
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
